import type { InvoiceImportEvidence } from "../../shared/invoice-settlement";
import type { InvoiceDueDateProvenance, InvoiceTerm } from "../../shared/invoice-terms";
import type { DocumentBusinessDetails } from "../../shared/document-business-details";
import type { RichBlock } from "../../shared/document-line-content";
import type { CalculatedTax, DocumentTaxes, DocumentTaxSet } from "../../shared/document-taxes";

export type DiscountInput =
  | { kind: "percentage"; basisPoints: number; label: string | null }
  | { kind: "fixed_amount"; amountMinor: number; label: string | null };

export interface InvoiceLineInput {
  description: string;
  title?: string | null;
  details?: RichBlock[] | null;
  taxMode?: "inherit" | "none" | "override";
  taxSet?: DocumentTaxSet;
  quantityThousandths: number | null;
  unitAmountMinor: number | null;
  taxRateId: string | null;
}

export interface InvoiceDraftInput {
  taxModelVersion?: 1 | null;
  taxes?: DocumentTaxes;
  number?: string | null;
  numberManuallyEdited?: boolean;
  clientId: string | null;
  recipientContactId: string | null;
  issueDate: string;
  dueDate?: string;
  termLabel?: InvoiceTerm;
  note: string | null;
  reference?: string | null;
  paymentInstructions?: string | null;
  discount: DiscountInput | null;
  lines: InvoiceLineInput[];
  defaultTaxRateId?: string | null;
}

export interface InvoiceTaxRate {
  id: string;
  label: string;
  rateBasisPoints: number;
  state: "active" | "archived";
  version: number;
  taxNumber?: string | null;
}

export interface CalculatedInvoiceLine extends InvoiceLineInput {
  taxBreakdown?: readonly CalculatedTax[];
  id?: string;
  position: number;
  lineSubtotalMinor: number;
  discountAllocationMinor: number;
  taxableBaseMinor: number;
  taxAmountMinor: number;
  taxRate: InvoiceTaxRate | null;
}

export interface InvoiceDraftRecord {
  importEvidence?: InvoiceImportEvidence;
  /** Public-safe immutable settlement fact; never a payment. */
  historicalSettlement?: { paidOn: string | null };
  taxModelVersion?: 1 | null;
  taxes?: DocumentTaxes;
  id: string;
  businessDetails?: DocumentBusinessDetails | null;
  numberManuallyEdited?: boolean;
  clientId: string | null;
  clientName: string | null;
  recipientContactId: string | null;
  recipientName: string | null;
  documentState: "draft" | "issued"; documentRevision?: number; number?: string | null; convertedInvoiceId?: string | null; archivedAt?: string | null; paidMinor?: number; receivedLast30Minor?: number; sentAt?: string | null; issuer?: DocumentBusinessDetails; billingAddress?: { line1: string | null; line2: string | null; locality: string | null; region: string | null; postalCode: string | null; country: string | null };
  currency: "USD";
  issueDate: string;
  dueDate: string;
  dueDateProvenance: InvoiceDueDateProvenance;
  termLabel: InvoiceTerm;
  note: string | null;
  reference?: string | null;
  paymentInstructions?: string | null;
  discount: DiscountInput | null;
  lines: CalculatedInvoiceLine[];
  defaultTaxRateId?: string | null;
  subtotalMinor: number;
  discountTotalMinor: number;
  taxTotalMinor: number;
  grandTotalMinor: number;
  version: number;
  updatedAt: string;
}

export class InvoiceInputError extends Error {}
export class InvoiceNotFoundError extends Error {}
export class InvoiceConflictError extends Error {}
export class InvoiceRevisionError extends Error {}
